import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import { getInitials } from '@airclou/utils';
const sizeClasses = {
    nano: 'w-4 h-4 text-[0.5rem]',
    xs: 'w-5 h-5 text-[0.65rem]',
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-xs',
    lg: 'w-10 h-10 text-sm',
    profile: 'w-11 h-11 text-sm',
    xl: 'w-12 h-12 text-sm',
    '2xl': 'w-16 h-16 text-base'
};
const variantClasses = {
    default: 'bg-primary text-primary-foreground',
    gray: 'bg-gray-300 dark:bg-gray-400 text-gray-700 dark:text-gray-900',
    blue: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
    green: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
    red: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
    yellow: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
    purple: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
};
export const Avatar = memo(({ name, size = 'md', variant = 'default', className, showBorder = false, bgColor, textColor, src, alt }) => {
    const imgRef = useRef(null);
    /**
     * A photo that failed to load. Held in state rather than swapped in by
     * rewriting the container's `innerHTML`, which is what this used to do: that
     * detaches the `<img>` React owns, so when the same `<Avatar>` is later given
     * a working `src` — a member replaces a dead profile photo and a reused row
     * re-renders — React patches a node no longer in the document and the new
     * photo never appears. Held as the address rather than a bare flag, so a
     * replaced photo is always given its own chance without the caller having to
     * remember a `key`.
     */
    const [failedSrc, setFailedSrc] = useState(null);
    const initials = getInitials(name);
    const sizeClass = sizeClasses[size];
    const variantClass = variantClasses[variant];
    const avatarClasses = cn('rounded-full flex items-center justify-center font-medium flex-shrink-0', sizeClass, !bgColor && !textColor && variantClass, showBorder && 'border-2 border-white dark:border-gray-900 shadow-sm', className);
    const customStyles = {
        ...(bgColor && { backgroundColor: bgColor }),
        ...(textColor && { color: textColor })
    };
    // Prevent image flickering by controlling visibility
    useEffect(() => {
        if (src && imgRef.current) {
            // Hide the image initially
            imgRef.current.style.visibility = 'hidden';
            // Check if image is already cached
            const img = new Image();
            img.src = src;
            if (img.complete) {
                // Image is cached, show immediately
                imgRef.current.style.visibility = 'visible';
            }
            else {
                // Wait for image to load
                img.onload = () => {
                    if (imgRef.current) {
                        imgRef.current.style.visibility = 'visible';
                    }
                };
            }
        }
    }, [src]);
    if (src && src !== failedSrc) {
        return (_jsx("div", { className: cn('rounded-full flex-shrink-0 overflow-hidden relative flex items-center justify-center', sizeClass, showBorder && 'border-2 border-white dark:border-gray-900 shadow-sm', className), style: {
                ...customStyles,
                // Use background color as placeholder while image loads
                backgroundColor: customStyles.backgroundColor || '#e5e7eb'
            }, children: _jsx("img", { ref: imgRef, src: src, alt: alt || `${name} avatar`, className: "w-full h-full object-cover", 
                // Prevent layout shift
                style: {
                    visibility: 'hidden' // Will be made visible by useEffect
                }, 
                // Use will-change to hint browser about upcoming changes
                onLoad: (e) => {
                    e.target.style.visibility = 'visible';
                }, onError: () => setFailedSrc(src) }) }));
    }
    return (_jsx("div", { className: avatarClasses, style: customStyles, title: name, children: _jsx("span", { children: initials }) }));
});
Avatar.displayName = 'Avatar';
// Add global CSS to prevent image flickering
if (typeof document !== 'undefined' && !document.getElementById('avatar-anti-flicker-styles')) {
    const style = document.createElement('style');
    style.id = 'avatar-anti-flicker-styles';
    style.textContent = `
    /* Prevent image flickering during React re-renders */
    img[src^="http"]:not([data-loading]) {
      will-change: transform;
    }
    
    /* Force browser to maintain image in memory */
    .avatar-image-preload {
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: 1px;
      height: 1px;
      opacity: 0;
      pointer-events: none;
    }
  `;
    document.head.appendChild(style);
}
// Export for convenience
export default Avatar;
//# sourceMappingURL=Avatar.js.map